import { X } from "lucide-react";
import FilePreview, { PreviewNote, type PreviewState } from "./FilePreview";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertDialogPortal,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";
import { Document } from "../types/order";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { updatePaymentStatus } from "../apis";
import { Spinner } from "components";
import { useParams } from "react-router-dom";

interface PaymentReviewProps {
    paymentProofUrl: Partial<Document>;
    supportingDocUrl: Partial<Document>;
    amount: number;
    children: React.ReactNode;
    paymentId: string;
}

const rejectionReasons = [
    "Incorrect amount",
    "Invalid document",
    "Document not clear",
    "Wrong payment method",
    "Other",
];

export default function PaymentReview({
    paymentProofUrl,
    supportingDocUrl,
    amount,
    paymentId,
    children,
}: Readonly<PaymentReviewProps>) {
    const [activeTab, setActiveTab] = useState("payment");
    // Approve waits until the proof has actually been shown.
    const [proofState, setProofState] = useState<PreviewState>("loading");
    const [supportState, setSupportState] = useState<PreviewState>("loading");
    const proofSeen = !!paymentProofUrl?.url && proofState === "loaded";
    // A supporting document, if there is one, has to have actually loaded before the payment is approved.
    const mustSeeSupporting = !!supportingDocUrl?.url && supportState !== "loaded";
    const [showRejectDialog, setShowRejectDialog] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [otherReason, setOtherReason] = useState("");
    const [isConsentChecked, setIsConsentChecked] = useState(false);
    const queryClient = useQueryClient();
    const order_id = useParams().id as string;

    const handleNext = () => {
        if (activeTab === "payment") {
            setActiveTab("supporting");
        }
    };

    const handlePrevious = () => {
        if (activeTab === "supporting") {
            setActiveTab("payment");
        }
    };

    const { isLoading, mutate } = useMutation({
        mutationFn: updatePaymentStatus,
        onSuccess: () => {
            toast.success('Payment status updated successfully');
            queryClient.invalidateQueries({ queryKey: ["orderDetails"] });
        },
        onError: (error) => {
            toast.error('Failed to update payment status', {
                description: error instanceof Error ? error.message : undefined,
            });
        }
    });

    const handleUpdateStatus = (status: string, notes: string) => {
        mutate({
            status,
            payments_id: paymentId,
            order_details_id: order_id,
            reason: notes,
        })
    }

    if (isLoading) return <Spinner />


    return (
        <AlertDialog>
            <AlertDialogTrigger>{children}</AlertDialogTrigger>
            <AlertDialogPortal>
                <AlertDialogOverlay className="bg-black-900/30" />
                <AlertDialogContent className="flex max-h-[92vh] max-w-4xl flex-col gap-0 overflow-hidden bg-white-A700 p-0 rounded-md">
                    <AlertDialogHeader className="flex shrink-0 flex-row items-center justify-between border-b px-6 pb-4 pt-5">
                        <AlertDialogTitle>Review Payment</AlertDialogTitle>
                        <AlertDialogCancel aria-label="Close" className="border-none p-0">
                            <X aria-hidden className="cursor-pointer h-4 w-4" />
                        </AlertDialogCancel>
                    </AlertDialogHeader>
                    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 pb-3 pt-3">
                        <Tabs
                            defaultValue="payment"
                            value={activeTab}
                            onValueChange={setActiveTab}
                        >
                            <TabsList className="p-0 gap-2">
                                <TabsTrigger
                                    value="payment"
                                    className={`flex gap-2 p-0 pb-2 ${activeTab === "payment" ? "text-blue-700 border-b-4 border-blue-700 " : "text-[#555555]"}`}
                                >
                                    <span className="text-sm font-medium font-inter leading-[16.9px] border rounded-md py-1 px-2.5 border-[#EFF4FF]">
                                        1
                                    </span>
                                    <p className=" text-sm font-medium font-inter leading-[16.9px]">
                                        Payment Proof
                                    </p>
                                </TabsTrigger>
                                {supportingDocUrl?.url ? (
                                <TabsTrigger
                                    value="supporting"
                                    className={`flex gap-2 p-0 pb-2 ${activeTab === "supporting" ? "text-blue-700 border-b-4 border-blue-700 " : "text-[#555555]"}`}
                                >
                                    <span className="text-sm font-medium font-inter leading-[16.9px] border rounded-md py-1 px-2.5 border-[#EFF4FF]">
                                        2
                                    </span>
                                    <p className="text-sm font-medium font-inter leading-[16.9px]">
                                        Supporting document
                                    </p>
                                </TabsTrigger>
                                ) : null}
                            </TabsList>

                            {activeTab === "payment" && (
                                <div className="mt-4 space-y-4">
                                    <Label>Amount received</Label>
                                    <div className="flex items-center gap-2.5 border max-w-[340px] rounded-md p-1.5">
                                        <span className="bg-[#F9F9F9] rounded p-2.5 text-[#666666] leading-[16.9px]">
                                            INR
                                        </span>
                                        <p className="">{amount.toString()}</p>
                                    </div>
                                </div>
                            )}
                            <TabsContent value="payment">
                                <div className="mt-4 flex flex-col gap-2">
                                    {paymentProofUrl?.url ? (
                                        <>
                                            <FilePreview
                                                key={`proof:${paymentProofUrl.url}`}
                                                url={paymentProofUrl.url as string}
                                                kind={String(paymentProofUrl.type ?? "").includes("pdf") ? "pdf" : "image"}
                                                name={paymentProofUrl.name || "Payment proof"}
                                                onState={setProofState}
                                            />
                                            <PreviewNote state={proofState} />
                                        </>
                                    ) : (
                                        <p role="status" className="rounded-lg border bg-muted p-6 text-center text-sm text-[#555555]">
                                            No proof is attached to this payment, so it cannot be approved. It can still be rejected.
                                        </p>
                                    )}
                                </div>
                            </TabsContent>
                            <TabsContent value="supporting">
                                <div className="mt-4 flex flex-col gap-2">
                                    <FilePreview
                                        key={`support:${supportingDocUrl?.url}`}
                                        url={supportingDocUrl?.url as string}
                                        kind={String(supportingDocUrl?.type ?? "").includes("pdf") ? "pdf" : "image"}
                                        name={supportingDocUrl?.name || "Supporting document"}
                                        onState={setSupportState}
                                    />
                                    <PreviewNote state={supportState} />
                                </div>
                            </TabsContent>
                        </Tabs>

                        {/* <div className="flex items-center gap-2 py-2">
                            <input
                                checked={isConsentChecked}
                                onChange={(e) => setIsConsentChecked(e.target.checked)}
                                className="rounded border-[#D2D2D2]"
                                type="checkbox"
                                name="consent"
                                id="consent"
                            />
                            <label
                                htmlFor="consent"
                                className="text-base select-none leading-[9.6px] text-[#555555] text-[#666666]"
                            >
                                I have confirmed the payment status from AD
                            </label>
                        </div> */}

                    </div>
                    <div className="shrink-0 border-t px-6 pb-4 pt-3">
                        {mustSeeSupporting && (
                            <p className="text-sm text-[#555555]">This payment has a supporting document. Look at it before approving.</p>
                        )}
                        {/* Actions */}
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex gap-4">
                                <Button
                                    variant="outline"
                                    className="border border-blue-700"
                                    size="icon"
                                    aria-label="Previous document"
                                    onClick={handlePrevious}
                                    disabled={activeTab === "payment"}
                                >
                                    <svg
                                        width="15"
                                        height="12"
                                        viewBox="0 0 15 12"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M14.1663 6H0.833008M0.833008 6L5.83301 1M0.833008 6L5.83301 11"
                                            stroke="#165FE3"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Button>
                                <Button
                                    variant="outline"
                                    className="border border-blue-700"
                                    size="icon"
                                    aria-label="Next document"
                                    onClick={handleNext}
                                    disabled={activeTab === "supporting"}
                                >
                                    <svg
                                        width="15"
                                        height="12"
                                        viewBox="0 0 15 12"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M0.83366 6H14.167M14.167 6L9.16699 11M14.167 6L9.16699 1"
                                            stroke="#165FE3"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Button>
                            </div>
                            <div className="flex items-center gap-4">
                                <Button
                                    variant="outline"
                                    onClick={() => setShowRejectDialog(true)}
                                    className="text-destructive hover:text-destructive border rounded-md border-[#C62828] text-[#C62828]"
                                >
                                    Reject
                                </Button>
                                <Button
                                    className="bg-blue-700 rounded-md text-white-A700 border border-blue-700"
                                    onClick={() => handleUpdateStatus("accept", "")}
                                    disabled={mustSeeSupporting || !proofSeen}
                                    title={!proofSeen ? "The proof has to be shown first" : mustSeeSupporting ? "Look at the supporting document first" : undefined}
                                >
                                    Approve
                                </Button>
                            </div>
                        </div>
                    </div>
                    {showRejectDialog && (
                        <AlertDialog open={true}>
                            <AlertDialogPortal>
                                <AlertDialogOverlay className="bg-black-900/30" />
                                <AlertDialogContent className="max-h-[92vh] overflow-y-auto bg-white-A700 rounded-md">
                                    <AlertDialogHeader className="text-left">
                                        <AlertDialogTitle>Payment rejection</AlertDialogTitle>
                                    </AlertDialogHeader>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>
                                                Select the reason for rejecting the payment{" "}
                                                <span className="text-[#555555]">
                                                    (Visible to partners)
                                                </span>
                                            </Label>
                                            <Select
                                                value={rejectReason}
                                                onValueChange={setRejectReason}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select a reason" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {rejectionReasons.map((reason) => (
                                                        <SelectItem key={reason} value={reason}>
                                                            {reason}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {rejectReason === "Other" && (
                                            <div className="space-y-2 flex flex-col">
                                                <Label>
                                                    Specify 'Other'{" "}
                                                    {/* <span className="text-[#555555]">
                            (Visible to the customer)
                          </span> */}
                                                </Label>
                                                <div>
                                                    <textarea
                                                        placeholder="Write a reason why you rejected the payment..."
                                                        className="border border-gray-400 rounded-[10px]  w-[80%]"
                                                        value={otherReason}
                                                        rows={5}
                                                        onChange={(e) => setOtherReason(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex justify-end gap-4 mt-4">
                                            <Button
                                                variant="outline"
                                                className="text-destructive hover:text-destructive border rounded-md border-blue-700 text-blue-700 "
                                                onClick={() => setShowRejectDialog(false)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                className="bg-[#FF0000] rounded-md text-white-A700 border border-[#FF0000]"
                                                disabled={
                                                    isLoading ||
                                                    !rejectReason ||
                                                    (rejectReason === "Other" && !otherReason)
                                                }
                                                onClick={() => {
                                                    handleUpdateStatus("reject", rejectReason === "Other" ? otherReason : rejectReason);
                                                    setShowRejectDialog(false);
                                                }}
                                            >
                                                Reject Payment
                                            </Button>
                                        </div>
                                    </div>
                                </AlertDialogContent>
                            </AlertDialogPortal>
                        </AlertDialog>
                    )}
                </AlertDialogContent>
            </AlertDialogPortal>
        </AlertDialog>
    );
}
